import styled, { css } from 'styled-components';


export const Container = styled.div`
  width: 100%;
  max-width: ${props => props.theme.spacing.maxWidth};
  margin: 0 auto;
  padding: ${props => props.py || 0} ${props => props.theme.spacing.containerPadding};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding-left: ${props => props.theme.spacing.md} !important;
    padding-right: ${props => props.theme.spacing.md} !important;
  }
`;

export const Heading = styled.h1`
  font-family: ${props => props.theme.typography.heading.fontFamily};
  font-weight: ${props => props.theme.typography.heading.fontWeight};
  line-height: ${props => props.theme.typography.lineHeights.heading};
  margin-bottom: ${props => props.$mb || props.theme.spacing.md};
  color: ${props => props.color || props.theme.colors.text};
  ${props => props.$maxWidth && css`max-width: ${props.$maxWidth};`}
  ${props => props.$align && css`text-align: ${props.$align};`}

  ${props => {
    switch (props.as) {
      case 'h1':
        return css`font-size: ${props.theme.typography.sizes.h1};`;
      case 'h2':
        return css`font-size: ${props.theme.typography.sizes.h2};`;
      case 'h3':
        return css`font-size: ${props.theme.typography.sizes.h3};`;
      case 'h4':
        return css`font-size: ${props.theme.typography.sizes.h4};`;
      case 'h5':
        return css`font-size: ${props.theme.typography.sizes.h5};`;
      case 'h6':
        return css`font-size: ${props.theme.typography.sizes.h6};`;
      default:
        return css`font-size: ${props.size || props.theme.typography.sizes.h3};`;
    }
  }}
`;

export const Text = styled.p`
  font-family: ${props => props.theme.typography.body.fontFamily};
  font-weight: ${props => props.weight || props.theme.typography.body.fontWeight};
  line-height: ${props => props.theme.typography.lineHeights.body};
  margin-bottom: ${props => props.$mb || props.mb || props.theme.spacing.md};
  color: ${props => props.color || props.theme.colors.text};
  font-size: ${props => {
    switch (props.size) {
      case 'large':
        return props.theme.typography.sizes.bodyLarge;
      case 'small':
        return props.theme.typography.sizes.bodySmall;
      default:
        return props.theme.typography.sizes.body;
    }
  }};
`;

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xl};
  border-radius: 100vh;
  font-family: ${props => props.theme.typography.body.fontFamily};
  font-weight: 600 !important;
  font-size: ${props => props.theme.typography.sizes.body};
  line-height: 1;
  text-align: center;
  cursor: pointer;
  transition: all ${props => props.theme.transitions.default};
  white-space: nowrap;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(143, 228, 198, 0.4);
  }
`;

export const Button = styled.button`
  ${buttonBase}

  /* Default (primary) button - works on both backgrounds */
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.bone};
  border-top: 1px solid ${props => props.theme.colors.bone};
  border-left: 1px solid ${props => props.theme.colors.bone};
  border-right: 1px solid ${props => props.theme.colors.bone};
  border-bottom: 3px solid ${props => props.theme.colors.bone};
  box-shadow: 0 3px 0 0 ${props => props.theme.colors.bone};

  &:hover {
    background-color: ${props => props.theme.colors.primaryHover};
    border-bottom-width: 1px;
    transform: translateY(2px);
    box-shadow: 0 1px 0 0 ${props => props.theme.colors.bone};
  }

  &:active {
    background-color: ${props => props.theme.colors.primaryPressed};
    border-bottom-width: 1px;
    transform: translateY(2px);
    box-shadow: 0 1px 0 0 ${props => props.theme.colors.bone};
  }

  /* Secondary button on dark background */
  ${props => props.$secondary && props.$isDark && css`
    background-color: transparent;
    border-top: 1px solid ${props.theme.colors.bone};
    border-left: 1px solid ${props.theme.colors.bone};
    border-right: 1px solid ${props.theme.colors.bone};
    border-bottom: 3px solid ${props.theme.colors.bone};
    box-shadow: 0 3px 0 0 ${props.theme.colors.bone};
    color: ${props.theme.colors.bone};

    &:hover {
      background-color: ${props.theme.colors.bone};
      color: ${props.theme.colors.charcoal};
      border-bottom-width: 1px;
      transform: translateY(2px);
      box-shadow: 0 1px 0 0 ${props.theme.colors.bone};
    }

    &:active {
      background-color: ${props.theme.colors.bone};
      color: ${props.theme.colors.charcoal};
      border-bottom-width: 1px;
      transform: translateY(2px);
      box-shadow: 0 1px 0 0 ${props.theme.colors.bone};
    }
  `}

  /* Secondary button on light background */
  ${props => props.$secondary && !props.$isDark && css`
    background-color: transparent;
    border-top: 1px solid ${props.theme.colors.charcoal};
    border-left: 1px solid ${props.theme.colors.charcoal};
    border-right: 1px solid ${props.theme.colors.charcoal};
    border-bottom: 3px solid ${props.theme.colors.charcoal};
    box-shadow: 0 3px 0 0 ${props.theme.colors.bone};
    color: ${props.theme.colors.charcoal};

    &:hover {
      background-color: ${props.theme.colors.primary};
      color: ${props.theme.colors.bone};
      border-top-color: ${props.theme.colors.charcoal};
      border-left-color: ${props.theme.colors.charcoal};
      border-right-color: ${props.theme.colors.charcoal};
      border-bottom-width: 1px;
      border-bottom-color: ${props.theme.colors.charcoal};
      transform: translateY(2px);
      box-shadow: 0 1px 0 0 ${props.theme.colors.bone};
    }

    &:active {
      background-color: ${props.theme.colors.primary};
      color: ${props.theme.colors.bone};
      border-top-color: ${props.theme.colors.charcoal};
      border-left-color: ${props.theme.colors.charcoal};
      border-right-color: ${props.theme.colors.charcoal};
      border-bottom-width: 1px;
      border-bottom-color: ${props.theme.colors.charcoal};
      transform: translateY(2px);
      box-shadow: 0 1px 0 0 ${props.theme.colors.bone};
    }
  `}

  ${props => props.$small && css`
    padding: ${props.theme.spacing.sm} ${props.theme.spacing.lg};
    font-size: ${props.theme.typography.sizes.bodySmall};
  `}

  ${props => props.$large && css`
    padding: ${props.theme.spacing.lg} ${props.theme.spacing.xxl};
    font-size: ${props.theme.typography.sizes.bodyLarge};
  `}
`; 